import { AloftDate, MLSID, expected, noop } from '@aloft/toolkit-shared';
import { Modal } from '@aloft/toolkit-sui';

import { useState } from 'react';

import { useAutofillProperty } from '../../../../hooks/useAutofillProperty.ts';
import { ToastOptions } from '../../../../hooks/useToaster/types.ts';
import { useToaster } from '../../../../hooks/useToaster/useToaster.tsx';
import { useZodForm } from '../../../../hooks/useZodForm.ts';
import { addComps } from '../../../../redux/features/actions/addComps.ts';
import {
  selectEffectiveDate,
  selectHasArvOption,
  selectOrder,
} from '../../../../redux/features/selectors/orderSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks.ts';
import { useUpdateOrderFeatures } from '../../../orderManagement/components/OrderTable/hooks/useUpdateOrderFeatures.ts';
import { AddCompsForm } from './internal/AddCompsForm.tsx';
import { AddComparablesForm, schAddComparablesForm } from './types.ts';

export const AddComparablesModal = ({
  onModalClose,
  isModalOpen,
  listingType,
}: {
  onModalClose: () => void;
  isModalOpen: boolean;
  listingType?: 'listing' | 'rentalListing';
}) => {
  const dispatch = useAppDispatch();
  const autofillProperty = useAutofillProperty();
  const updateOrderFeatures = useUpdateOrderFeatures();
  const effectiveDate = useAppSelector(selectEffectiveDate);
  const hasArvOption = useAppSelector(selectHasArvOption);
  const orderID = useAppSelector(selectOrder).id;

  const [isLoading, setIsLoading] = useState(false);
  const toast = useToaster();

  const autofillForm = useZodForm({
    schema: schAddComparablesForm,
    defaultValues: {
      // Without these empty strings, we get a warning that an
      // uncontrolled input is changing to a controlled input (even though
      // we do have defaults in the zod schema, which is a separate thing)
      addresses: [
        {
          AddressLineText: '',
          unitNumber: '',
          CityName: '',
          StateCode: '',
          PostalCode: '',
        },
      ],
      mlsListings: [
        {
          mls: '',
          mlsListingID: '',
        },
      ],
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const submitAutofill = autofillForm.handleSubmit(async (submitParams: AddComparablesForm) => {
    setIsLoading(true);
    const successToastOptions: ToastOptions = {
      type: 'success' as const,
      id: 'add-comps-via-import',
      title: 'Successfully added property!',
    };

    const handleCompletion = (toastOptions: ToastOptions) => {
      toast(toastOptions);
      setIsLoading(false);
      if (toastOptions.type === 'success') {
        onModalClose();
      }
    };

    try {
      const properties = await Promise.all([
        ...(
          submitParams.addresses?.filter((address) =>
            [
              address.AddressLineText,
              address.unitNumber,
              address.CityName,
              address.StateCode,
              address.PostalCode,
            ].some((field) => field !== ''),
          ) ?? []
        ).map((address) =>
          autofillProperty({
            address: expected(address),
            effectiveYear: AloftDate.schema.parse(expected(effectiveDate)).getYear(),
            orderID,
            compType: submitParams.compType,
            listingType,
          }),
        ),
        ...(
          submitParams.mlsListings?.filter(
            (mlsListing) => mlsListing.mls !== '' && mlsListing.mlsListingID !== '',
          ) ?? []
        ).map((mlsListing) =>
          autofillProperty({
            mlsListingInfo: {
              mls: expected(mlsListing.mls) as MLSID,
              mlsListingID: expected(mlsListing.mlsListingID),
            },
            effectiveYear: AloftDate.schema.parse(expected(effectiveDate)).getYear(),
            orderID,
            compType: submitParams.compType,
            listingType,
          }),
        ),
      ]);

      dispatch(addComps(properties));
      updateOrderFeatures(orderID, properties).then(noop).catch(console.error);
    } catch (e: unknown) {
      let errorTitle = 'Failed to add property.';
      if (e instanceof Error && e.message.includes('No property found from MLS listing')) {
        errorTitle = 'We could not find a property from the MLS listing info you provided.';
      }

      handleCompletion({
        type: 'error',
        id: 'add-comps-via-import-error',
        title: errorTitle,
      });

      return undefined;
    }

    return handleCompletion(successToastOptions);
  }, console.error);

  const autofillFields = autofillForm.watch();

  const isAutofillValid = () => {
    const addresses = autofillFields.addresses || [];
    const mlsListings = autofillFields.mlsListings || [];
    const emptyAddress =
      addresses.length === 1 &&
      !addresses[0]?.AddressLineText &&
      !addresses[0]?.CityName &&
      !addresses[0]?.StateCode &&
      !addresses[0]?.PostalCode;
    const emptyMlsListing =
      mlsListings.length === 1 && !mlsListings[0]?.mls && !mlsListings[0]?.mlsListingID;
    const { compType } = autofillFields;

    return (
      (addresses?.every(
        (address) =>
          address?.AddressLineText &&
          address?.CityName &&
          address?.StateCode &&
          address?.PostalCode,
      ) ||
        emptyAddress) &&
      (mlsListings?.every((mlsListing) => mlsListing?.mls && mlsListing?.mlsListingID) ||
        emptyMlsListing) &&
      (hasArvOption ? compType : true)
    );
  };

  return (
    <Modal
      size="lg"
      isOpen={isModalOpen}
      onClose={onModalClose}
      closeOnEsc
      title="Manually Add Comp"
      primaryAction={{
        id: 'add-btn',
        text: 'Add',
        onClick: () => {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          submitAutofill();
        },
        disabled: !isAutofillValid(),
        isLoading,
      }}
      secondaryAction={{
        id: 'cancel-btn',
        text: 'Cancel',
        onClick: () => {
          autofillForm.reset();
          onModalClose();
        },
      }}
    >
      <AddCompsForm form={autofillForm} />
      {autofillForm.formState.errors.addresses?.message && (
        <p>{autofillForm.formState.errors.addresses.message}</p>
      )}
      {autofillForm.formState.errors.mlsListings?.message && (
        <p>{autofillForm.formState.errors.mlsListings.message}</p>
      )}
    </Modal>
  );
};
